import type { GetProductsOptions } from '@kimuno/api-sdk/lib/services/product';
import type { Product } from '@kimuno/api-sdk/lib/types/responses/Product';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import { fetchProductsByQuery as fetchProductsByQueryAction } from '../../../actions/product.actions';
import {
	getProductsBySubjectIdStatus,
	makeGetProductsBySubjectId,
} from '../../../reducers/dashboard.selectors';
import type { SearchFilters } from '../../../types/search';
import type { CascadeLevel, FallbackLevel, TitleSegments } from '../../../utils/profileFilters';
import useAction from '../../../utils/useAction';
import useDashboardRowFetch, { MAX_ITEMS, MIN_ITEMS } from './useDashboardRowFetch';

// A row must have at least this many items to render; below it, the cascade
// relaxes to the next level (and the row hides once the final level is still short).
export const PROFILE_ROW_MIN_ITEMS = MIN_ITEMS;
// Hard cap on how many items a row ever displays.
export const PROFILE_ROW_MAX_ITEMS = MAX_ITEMS;

const ROW_QUERY_OPTIONS: GetProductsOptions = {
	limit: PROFILE_ROW_MAX_ITEMS,
	sort: 'desc',
	sortBy: 'createdDate',
};

export interface ProfileProductRow {
	products: Product[];
	status: AsyncActionStatus;
	effectiveLevel: FallbackLevel;
	effectiveFilters: SearchFilters;
	titleSegments: TitleSegments;
	// True when the cascade cannot relax any further; the consumer only hides a
	// short row once this is reached (avoids flicker while still cascading).
	isFinalLevel: boolean;
}

/**
 * Drives one profile-aware product row through the fallback cascade built in
 * Phase 1. Fetches the strictest level; when a resolved level yields fewer than
 * PROFILE_ROW_MIN_ITEMS, it advances to the next relaxed level and fetches that.
 *
 * Each level is cached under its own key (via the level's `keyPrefix`), so a level
 * change never reads a stale result from a different level and never re-requests a
 * level it already has. Re-runs from the strictest level whenever the cascade
 * identity changes (profile change).
 */
const useProfileProductRow = (cascade: CascadeLevel[]): ProfileProductRow => {
	const fetchProductsByQuery = useAction(fetchProductsByQueryAction);

	// Stable identity of this cascade's filter set — the per-level keyPrefixes minus
	// nothing, so a profile change (subject / years / type / publisher) resets to L0.
	const signature = useMemo(() => cascade.map((level) => level.keyPrefix).join('|'), [cascade]);

	// Track the level alongside the signature it belongs to. Resetting to L0 in an
	// effect would let the fetch effect (below) fire once for the new cascade at the
	// *old* level index before the reset commits — a spurious wrong-level request.
	// Reset synchronously during render (React's derived-state pattern) instead, so
	// no effect ever observes the stale index.
	const [state, setState] = useState({ signature, levelIndex: 0 });

	if (state.signature !== signature) {
		setState({ signature, levelIndex: 0 });
	}
	const levelIndex = state.signature === signature ? state.levelIndex : 0;

	const safeIndex = Math.min(levelIndex, cascade.length - 1);
	const currentLevel = cascade[safeIndex];
	// Matches the reducer key `${keyPrefix}-${subjects.join(',')}` — the row may span
	// several subjects (the Studyflix row combines the user's top subjects).
	const subjectId = currentLevel.query.subjects?.join(',') ?? '';
	const keyPrefix = currentLevel.keyPrefix;

	// Per-instance selector: each row keeps a stable products reference for its own
	// (subjectId, keyPrefix) pair across unrelated store updates (the shared selector
	// would thrash its single-entry cache as sibling rows read different pairs).
	const selectProducts = useMemo(() => makeGetProductsBySubjectId(), []);
	const products = useSelector((state) => selectProducts(state, subjectId, keyPrefix));
	const status = useSelector((state) => getProductsBySubjectIdStatus(state, subjectId, keyPrefix));

	// Each level is cached under its own key, so a remount that finds the level
	// already fetched (READY/LOADING) skips the request; a never-fetched level
	// (INITIAL) or a level that last errored (ERROR, retried on remount) triggers a
	// call. A profile change produces a new key whose status is INITIAL, so it
	// refetches as expected. No subjects → nothing to fetch.
	useDashboardRowFetch({
		fetchKey: `${signature}|${safeIndex}`,
		status,
		enabled: Boolean(subjectId),
		fetch: () => fetchProductsByQuery(currentLevel.query, ROW_QUERY_OPTIONS),
	});

	// Advance to the next relaxed level when this one resolved short.
	useEffect(() => {
		if (
			status === AsyncActionStatus.READY &&
			products.length < PROFILE_ROW_MIN_ITEMS &&
			safeIndex < cascade.length - 1
		) {
			setState({ signature, levelIndex: safeIndex + 1 });
		}
	}, [status, products.length, safeIndex, cascade.length, signature]);

	return {
		products: products.slice(0, PROFILE_ROW_MAX_ITEMS),
		status,
		effectiveLevel: currentLevel.level,
		effectiveFilters: currentLevel.searchFilters,
		titleSegments: currentLevel.titleSegments,
		isFinalLevel: safeIndex === cascade.length - 1,
	};
};

export default useProfileProductRow;
